import type { Block, Cell, ChipCell, Column, KvItem, Tone } from '@/kova/spec/types'

import type { ApiClient } from '@/lib/api'

import type { ScreenData, ScreenLoader } from './provenance'
import {
  asArray, asRecord, attempt, disclosure, knows, malformed, moneyCell, moneyReadable,
  readBool, readDay, readDuration, readInstant, readInt, readText, receipt, refLine,
  spanOf, summarise,
  type Attempt,
} from './wire'

/**
 * Money — the movement explorer and the outbound queue (go-wallet#772; this
 * console #147).
 *
 *   mny-tx        GET /v1/admin/movements
 *   mny-cashouts  GET /v1/admin/cash-outs
 *                 GET /v1/admin/cash-outs/{id}   (row opener)
 *
 * # `mny-internal` is absent and is not this change's to add
 *
 * There is no admin read for internal transfers. `internal-transfers` is a
 * CUSTOMER-surface POST; no admin route lists them and none reads one. The
 * screen keeps its not-built notice, which is the honest answer — and it is the
 * screen the design most wants, because two postings with no provider book
 * reference is the unlicensed-issuance case.
 *
 * # `mny-cashouts` — the corpus's headline cannot be served
 *
 * The designed screen leads with a red note: "1 cash-out is past SLA with no
 * provider confirmation". NOTHING IN THIS DATABASE HOLDS A CASH-OUT SLA or a
 * `past_sla` predicate, so a threshold here would be one the console and the
 * backend each invented separately — and the operator would be reading a
 * breach against a promise nobody made. The ambiguous case is still the point
 * of the screen and is still surfaced, as what the data supports: submitted,
 * no transfer reference, and how long it has been that way.
 *
 * Two whole columns go with it:
 *
 *   - SCA. This database holds `challenge_ref` and `sca_completed_at` and no
 *     others — no state, no failure vocabulary, no expiry, no challenge table.
 *     `passed`, `failed` and a challenge the customer abandoned have NO
 *     representation, and a null "must not render as a pass, a failure or a
 *     timeout". A non-null completion is not a state either: it records when WE
 *     OBSERVED THE INSTITUTION REPORT a completion, never when the customer
 *     touched their device.
 *   - The R-transaction reason. No return column, no R-transaction enum and no
 *     mapping table exists in any migration. `decline_reason` is NOT that
 *     column and must not be labelled as one: it is the institution's VERBATIM
 *     text, present exactly on a `declined` row, "never a code such as AC04,
 *     never mapped to human copy". A payment returned after settling — what an
 *     R-transaction actually describes — has no representation at all.
 *
 * And the states are six, as stored, never mapped onto the design's five.
 * `processing` is exactly the state the schema refuses to have: "a state
 * meaning we handed it over and do not know what happened is the state a UI
 * renders as done". `returned` cannot be represented. `abandoned` means OUR
 * SWEEP RAN after a 48-hour default — never that the institution expired
 * anything.
 *
 * # `mny-tx` — no export, no name, and no posting amount
 *
 * The designed export block is not served and cannot be: there is no export
 * table, no worker and no bucket in any deployment, so a button would queue
 * into nothing. There is no customer NAME on this wire, and `via_masspay` is an
 * orchestrator id the response cannot produce. And the posting carries NO
 * AMOUNT deliberately — the figures are on the legs, and which leg is "the
 * amount" depends on whose side you read from, so inventing one would put a
 * number on the screen an operator quotes from that means something different
 * for a transfer than for a credit.
 *
 * A redacted counterparty renders as REDACTED, never as absent: a blank reads
 * as an institution that sent no payer details, which is a claim about the
 * institution rather than about us.
 *
 * # Nothing here moves money, and neither screen offers to
 *
 * There is no repost, repair or replay on the movement feed at any permission:
 * an unposted event is repaired through the ordinary ingest path, which writes
 * the posting, the enrichment task and the customer notification in ONE
 * transaction, and a button here "would insert a posting directly, fix the
 * balance, and never tell the customer their money arrived". There is no
 * release, retry, cancel or resubmit on the cash-out queue, and the corpus's
 * actions block — "Query provider by idempotency key", "Open case" — names two
 * routes that do not exist.
 *
 * # Law 12
 *
 * Both reads are Postgres, over `provider_events` and `ledger_postings`. The
 * corpus titles the movement table `mirror_movements`, which reads as the
 * ClickHouse mirror and is not — so the block is `src: 'ledger'` and nothing
 * here carries an analytics badge or a mirror freshness stamp.
 */

// ---------------------------------------------------------------------------
// Paths
// ---------------------------------------------------------------------------

export const MOVEMENTS_PATH = '/movements'
export const CASH_OUTS_PATH = '/cash-outs'

export function cashOutPath(id: string): string {
  return `${CASH_OUTS_PATH}/${encodeURIComponent(id)}`
}

const MOVEMENTS_SOURCE = 'postgres · provider_events (GET /v1/admin/movements)'
const CASH_OUTS_SOURCE = 'postgres · cash_outs (GET /v1/admin/cash-outs)'

/** The event is the institution's; the posting is ours. */
const MOVEMENT_AUTHORITY =
  'operational — the event is the institution’s record, the posting is ours'
const CASH_OUT_AUTHORITY =
  'operational — a settlement is the institution’s fact, never an operator’s opinion'

function unread(
  kicker: string, title: string, body: string, failed: Extract<Attempt, { ok: false }>,
  source: string,
): ScreenData {
  return {
    blocks: [{
      t: 'note', tone: 'r', kicker, title,
      body: `${failed.reason} ${body}`,
      ref: refLine(failed, 'Nothing on this screen was read.'),
    }],
    provenance: { kind: 'unread', source, reason: failed.reason },
  }
}

// ===========================================================================
// mny-tx — the movement explorer
// ===========================================================================

/**
 * Movement states, from the institution's own vocabulary.
 *
 * `reversed` is critical because it is money going back out; `authorised` is
 * attention because it is not settled yet and the design's own filter set
 * treats the two as different questions.
 */
const MOVEMENT_STATE_TONE: Readonly<Record<string, Tone>> = {
  settled: 'g',
  authorised: 'a',
  authorized: 'a',
  pending: 'a',
  reversed: 'r',
  declined: 'r',
}

/**
 * The columns.
 *
 * The corpus draws ID / Value date / Customer / Rail / Counterparty / Platform
 * / Amount / State / Provider ref. Two amounts replace one, because the wire
 * carries `authorised` and `settled` separately and they are different facts —
 * an authorised movement that never settled is exactly what an operator is
 * looking for on this screen.
 *
 * `Posted` is new and is the point of the read: an event the institution says
 * arrived, with nothing in the journal behind it, is money the customer cannot
 * see.
 */
const MOVEMENT_COLS: readonly Column[] = [
  ['Event', 'm', '96px'],
  ['Value date', 'm', '110px'],
  ['Customer', 'm', '104px'],
  ['Rail', 'x', '104px'],
  ['Counterparty', 'x', 'minmax(190px,1.4fr)'],
  ['Authorised', 'a', '140px'],
  ['Settled', 'a', '140px'],
  ['State', 'c', '112px'],
  ['Recognition', 'c', '150px'],
  ['Posted', 'c', '150px'],
  ['Institution ref', 'm', '160px'],
]

/**
 * The counterparty, and the difference between erased and never sent.
 *
 * Art. 17 erasure clears the payer's details and stamps `counterparty_redacted`.
 * The two look identical on the wire unless the stamp is carried, and an erased
 * counterparty rendered as a blank reads as an institution that sent no payer
 * details — a claim about the institution rather than about us.
 */
function counterparty(o: Record<string, unknown>): Cell {
  if (readBool(o, 'counterparty_redacted') === true) {
    const at = readInstant(o, 'counterparty_redacted_at')
    return at.kind === 'at' ? `— erased under a privacy request, ${at.label}` : '— erased under a privacy request'
  }
  const name = readText(o, 'counterparty_name')
  const iban = readText(o, 'counterparty_iban_masked')
  if (name === null && iban === null) return '— the institution sent no payer details'
  if (name === null) return iban ?? ''
  return iban === null ? name : `${name} · ${iban}`
}

/**
 * Recognition, and the two absences that are opposite claims.
 *
 * `recognition_decided: false` means recognition NEVER RAN on this event, which
 * on the credit path means the customer was told about money with no payer
 * attached. `recognition_redacted: true` means it ran and Art. 17 has since
 * been through — the opposite claim about whether it ran.
 */
function recognition(o: Record<string, unknown>, gaps: string[]): ChipCell {
  const decided = readBool(o, 'recognition_decided')
  if (decided === null) {
    gaps.push('whether recognition ran on an event')
    return ['unstated', 'a']
  }
  if (readBool(o, 'recognition_redacted') === true) return ['erased, but it ran', 'n']
  if (!decided) return ['never ran', 'a']
  const confidence = readText(o, 'recognition_confidence')
  const platform = readText(o, 'recognition_platform_id')
  if (platform === null) return [confidence ?? 'decided', 'n']
  return [confidence === null ? platform : `${platform} · ${confidence}`, 'g']
}

const MOVEMENT_MISSING: readonly string[] = [
  'an export — there is no export table, no export worker and no replicated bucket in any deployment, so the designed export would queue into nothing',
  'the posting’s amount — a posting holds none; the figures are on its legs, and which leg is “the amount” depends on whose side you read from',
  'the customer’s name — this read carries a customer id only',
  'the orchestrator a movement was routed through — the designed platform column’s via_masspay value is an orchestrator id this response cannot produce',
]

export function composeMovements(read: Attempt, at: Date): ScreenData {
  if (!read.ok) {
    return unread(
      'Transactions', 'The movement feed could not be read',
      'No movement below is known — that is different from no movement matching the view.',
      read, MOVEMENTS_SOURCE,
    )
  }

  const o = asRecord(read.payload)
  const list = o === null ? null : asArray(o['movements'])
  if (o === null || list === null) {
    return unread(
      'Transactions', 'The movement feed could not be read',
      'The feed returned a shape this console does not know.',
      malformed('The feed returned a shape this console does not know.'), MOVEMENTS_SOURCE,
    )
  }

  const gaps: string[] = []
  const rows: (readonly Cell[])[] = []
  let unreadable = 0
  let unposted = 0
  let unrecognised = 0

  for (const raw of list) {
    const m = asRecord(raw)
    if (m === null) { unreadable += 1; continue }

    const id = readInt(m, 'id')
    if (id.kind !== 'exact') gaps.push('an event id')

    const valueDate = readDay(m, 'value_date')
    if (valueDate === null) gaps.push('a value date')

    const customer = readInt(m, 'customer_id')
    if (customer.kind !== 'exact') gaps.push('a customer id')

    const rail = readText(m, 'rail')
    if (rail === null) gaps.push('a rail')

    // Two amounts, two facts. An authorised movement that never settled is the
    // row an operator is looking for, and folding them loses it.
    if (asRecord(m['authorised']) !== null && !moneyReadable(m, 'authorised')) {
      gaps.push('an authorised amount')
    }
    if (asRecord(m['settled']) !== null && !moneyReadable(m, 'settled')) {
      gaps.push('a settled amount')
    }
    const authorised = asRecord(m['authorised']) === null
      ? '— none authorised'
      : moneyCell(m, 'authorised')
    const settled = asRecord(m['settled']) === null
      ? '— not settled'
      : moneyCell(m, 'settled')

    const state = readText(m, 'state')
    if (state === null) gaps.push('a movement state')
    const stateCell: ChipCell = state === null
      ? ['unstated', 'a']
      : knows(MOVEMENT_STATE_TONE, state)
        ? [state, MOVEMENT_STATE_TONE[state] ?? 'a']
        : [state, 'a']

    const recognitionCell = recognition(m, gaps)
    if (recognitionCell[0] === 'never ran') unrecognised += 1

    // The finding this screen exists to surface: the institution says money
    // arrived and the journal does not show it.
    const posted = readBool(m, 'posted')
    if (posted === null) gaps.push('whether an event produced a posting')
    const postingID = readInt(m, 'posting_id')
    const age = readDuration(m, 'unposted_age_seconds')
    let postedCell: ChipCell
    if (posted === null) {
      postedCell = ['unstated', 'a']
    } else if (posted) {
      postedCell = [postingID.kind === 'exact' ? `posting ${String(postingID.value)}` : 'posted', 'g']
    } else {
      unposted += 1
      postedCell = [age.kind === 'exact' ? `unposted ${spanOf(age.value)}` : 'unposted', 'r']
    }

    rows.push([
      id.kind === 'exact' ? String(id.value) : '— no id',
      valueDate ?? '—',
      customer.kind === 'exact' ? String(customer.value) : '—',
      rail ?? '—',
      counterparty(m),
      authorised,
      settled,
      stateCell,
      recognitionCell,
      postedCell,
      readText(m, 'institution_event_id') ?? '— none',
    ])
  }

  const hasMore = readBool(o, 'has_more') === true

  const footer = rows.length === 0
    ? (unreadable === 0
        ? 'No movement matches this view. The feed was read and is empty for these filters.'
        : `No movement could be read: ${String(unreadable)} came back in a shape this console does not know.`)
    : `${String(rows.length)} ${rows.length === 1 ? 'movement' : 'movements'}${unposted === 0 ? '' : ` · ${String(unposted)} with no posting behind them`}${hasMore ? ' · the backend caps the page' : ''}${unreadable === 0 ? '' : ` · ${String(unreadable)} row(s) could not be read`}`

  const blocks: Block[] = [{
    // NOT `analytics`. The corpus titles this `mirror_movements`, which reads
    // as the ClickHouse mirror; the query is over `provider_events` and
    // `ledger_postings` in Postgres — law 12, in the direction that is easy to
    // get wrong because the wrong answer looks more careful.
    t: 'table', title: 'Movements', src: 'ledger',
    // The corpus draws six applied facets including a currency-scoped amount
    // range. This read applies none, and a facet bar is a claim that one is.
    facets: false,
    cols: MOVEMENT_COLS, rows, footer,
  }]

  // Unposted depth is counted over the WHOLE table under the same filters,
  // never over the page: a count over a page of fifty renders as a confident
  // number about a table of thousands, and on this question that number decides
  // whether somebody investigates.
  const depth = readInt(o, 'unposted_depth')
  if (depth.kind === 'exact' && depth.value > 0) {
    blocks.push({
      t: 'note', tone: 'r', kicker: 'Unposted',
      title: `${String(depth.value)} event${depth.value === 1 ? '' : 's'} the journal does not show`,
      body: 'The institution says the money arrived and there is no posting behind it, so the customer cannot see it. Counted over the whole table, not this page.',
      ref: 'unposted_depth · repaired through the ordinary ingest path, never from here',
    })
  }

  if (unrecognised > 0) {
    blocks.push({
      t: 'note', tone: 'a', kicker: 'Recognition',
      title: `${String(unrecognised)} movement${unrecognised === 1 ? '' : 's'} recognition never ran on`,
      body: 'On the credit path that means the customer was told about money with no payer attached. It is not the same as a payer that was erased afterwards.',
      ref: 'recognition_decided = false',
    })
  }

  const postingNote = disclosure(
    o, 'posting_note', 'Amounts', 'Why the posting carries no amount', 'n',
  )
  if (postingNote !== null && rows.length > 0) blocks.push(postingNote)

  const missing = [...MOVEMENT_MISSING, ...summarise(gaps)]
  if (unreadable > 0) missing.push(`${String(unreadable)} movement(s) this console could not read`)

  return {
    blocks,
    provenance: {
      kind: 'partial', source: MOVEMENTS_SOURCE, authority: MOVEMENT_AUTHORITY,
      readAt: receipt(at), missing,
    },
    title: 'Transactions',
    subject: `provider_events · ${String(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}`,
  }
}

export const loadMovements: ScreenLoader = async (api) =>
  composeMovements(
    await attempt(() => api.get<unknown>(MOVEMENTS_PATH), 'The movement feed'),
    new Date(),
  )

// ===========================================================================
// mny-cashouts — the outbound queue
// ===========================================================================

/**
 * Six states, as stored, never mapped onto a shorter set.
 *
 * The designed lifecycle — `submitted → processing → settled / returned /
 * rejected` — shares two of them. `processing` is exactly the state the schema
 * refuses to have: "a state meaning we handed it over and do not know what
 * happened is the state a UI renders as done". `returned` cannot be represented
 * at all. `rejected` is `declined`, which is the institution's decision
 * carrying the institution's reason.
 *
 * `abandoned` is critical rather than neutral: it means OUR sweep ran after a
 * 48-hour default with no authorisation, so it is a payment nobody completed
 * rather than one anybody decided about.
 */
const CASH_OUT_STATE_TONE: Readonly<Record<string, Tone>> = {
  awaiting_authorisation: 'n',
  authorised: 'a',
  submitted: 'a',
  settled: 'g',
  declined: 'r',
  abandoned: 'a',
}

/**
 * The columns.
 *
 * The corpus draws ID / Customer / Amount / State / SCA / Idempotency key /
 * Provider ref / decision_ref / R-reason. **SCA and R-reason are dropped** —
 * neither has a schema behind it, and the values the corpus shows for both are
 * unservable.
 *
 * `Decline reason` replaces R-reason and is a different thing said plainly: the
 * institution's verbatim text, present exactly on a declined row. It is never a
 * code and never mapped to friendlier copy.
 *
 * `Waiting` replaces the SLA bar. There is no SLA in this database, so what is
 * drawn is how long the payment has been submitted with no transfer reference —
 * a fact, where a breach would have been an invention.
 */
const CASH_OUT_COLS: readonly Column[] = [
  ['Payment', 'm', '96px'],
  ['Customer', 'm', '104px'],
  ['Amount', 'a', '146px'],
  ['State', 'c', '160px'],
  ['Submitted', 'm', '150px'],
  ['Provider transfer', 'm', 'minmax(160px,1.2fr)'],
  ['Idempotency key', 'm', 'minmax(180px,1.2fr)'],
  ['decision_ref', 'm', '170px'],
  ['Decline reason', 'x', 'minmax(180px,1.3fr)'],
]

interface CashOutRead {
  readonly cells: readonly Cell[]
  readonly gaps: readonly string[]
  readonly id: string | null
  /** Submitted, and the institution has returned neither a reference nor a decision. */
  readonly awaitingProvider: boolean
}

function readCashOutRow(raw: unknown): CashOutRead | null {
  const o = asRecord(raw)
  if (o === null) return null
  const gaps: string[] = []

  const id = readInt(o, 'id')
  if (id.kind !== 'exact') gaps.push('a payment id')

  const customer = readInt(o, 'customer_id')
  if (customer.kind !== 'exact') gaps.push('a customer id')

  if (!moneyReadable(o, 'amount')) gaps.push('an amount')

  const state = readText(o, 'state')
  if (state === null) gaps.push('a payment state')
  const known = state !== null && knows(CASH_OUT_STATE_TONE, state)
  if (state !== null && !known) gaps.push('a payment state this console recognises')
  // `abandoned` says our constant elapsed, never that the institution expired
  // anything, and the chip says so rather than leaving the word to be read as
  // the customer having given up.
  const stateLabel = state === 'abandoned' ? 'abandoned · our 48h sweep ran' : (state ?? 'unstated')
  const stateCell: ChipCell = [stateLabel, known ? (CASH_OUT_STATE_TONE[state] ?? 'a') : 'a']

  const submitted = readInstant(o, 'submitted_at')
  const transfer = readText(o, 'transfer_ref')

  // The ambiguous case, from what the data supports: submitted, and the
  // institution has returned neither a transfer reference nor a decision.
  // Deliberately NOT "past SLA" — nothing in this database holds one.
  const awaitingProvider = state === 'submitted' && transfer === null

  const idempotency = readText(o, 'idempotency_key')
  if (idempotency === null) gaps.push('an idempotency key')

  // Law 13. The reference a support agent quotes when a customer asks why.
  const decision = readText(o, 'decision_ref')
  if (decision === null) gaps.push('a decision reference')

  // The institution's VERBATIM text, present exactly on a declined row. Never a
  // code such as AC04 and never mapped to human copy — migration 018 is
  // explicit that a decline is their decision carrying their reason, "not a
  // transport error to swallow, and not ours to reinterpret into something
  // friendlier".
  const decline = readText(o, 'decline_reason')

  return {
    cells: [
      id.kind === 'exact' ? String(id.value) : '— no id',
      customer.kind === 'exact' ? String(customer.value) : '—',
      moneyCell(o, 'amount'),
      stateCell,
      submitted.kind === 'at' ? submitted.label : submitted.kind === 'absent' ? 'not submitted' : '—',
      // A submitted payment with no reference is the row this screen is for,
      // and it says what it is rather than showing a blank.
      transfer ?? (awaitingProvider ? '— the institution has returned none' : '—'),
      idempotency ?? '— none',
      decision ?? '— none quoted',
      decline ?? '',
    ],
    gaps,
    id: id.kind === 'exact' ? String(id.value) : null,
    awaitingProvider,
  }
}

const CASH_OUT_MISSING: readonly string[] = [
  'the SCA lifecycle — this database holds a challenge reference and a completion stamp and nothing else, so passed, failed and an abandoned challenge have no representation, and a null must not render as any of them',
  'an R-transaction reason — no return column, no enum and no mapping table exists; a payment returned after settling has no representation at all, and the decline reason is a different field carrying the institution’s own words',
  'an SLA and a past-SLA verdict — nothing in this database holds either, so a breach shown here would be a threshold this console invented',
  'our cost for the rail — the buy price is on the row and belongs to a permission the readers of this queue do not hold',
]

export function composeCashOuts(read: Attempt, at: Date): ScreenData {
  if (!read.ok) {
    return unread(
      'Cash-outs', 'The outbound queue could not be read',
      'No payment below is known — that is different from nothing being in flight.',
      read, CASH_OUTS_SOURCE,
    )
  }

  const o = asRecord(read.payload)
  const list = o === null ? null : asArray(o['cash_outs'])
  if (o === null || list === null) {
    return unread(
      'Cash-outs', 'The outbound queue could not be read',
      'The queue returned a shape this console does not know.',
      malformed('The queue returned a shape this console does not know.'), CASH_OUTS_SOURCE,
    )
  }

  const gaps: string[] = []
  const rows: (readonly Cell[])[] = []
  const rowKeys: (string | null)[] = []
  let unreadable = 0
  let awaiting = 0

  for (const raw of list) {
    const row = readCashOutRow(raw)
    if (row === null) { unreadable += 1; continue }
    rows.push(row.cells)
    rowKeys.push(row.id)
    gaps.push(...row.gaps)
    if (row.awaitingProvider) awaiting += 1
  }

  const hasMore = readBool(o, 'has_more') === true

  const footer = rows.length === 0
    ? (unreadable === 0
        ? 'No cash-out matches this view. The queue was read and is empty.'
        : `No payment could be read: ${String(unreadable)} came back in a shape this console does not know.`)
    : `${String(rows.length)} ${rows.length === 1 ? 'payment' : 'payments'} · states are shown as stored, never mapped onto a shorter set${hasMore ? ' · the backend caps the page' : ''}${unreadable === 0 ? '' : ` · ${String(unreadable)} row(s) could not be read`}`

  const blocks: Block[] = []

  // The ambiguous case leads, because it is what the screen is for — but said
  // as what the data supports rather than as a breach. A hold that the
  // institution has not confirmed is drawn as PENDING, never as sent.
  if (awaiting > 0) {
    blocks.push({
      t: 'note', tone: 'a', kicker: 'Awaiting the institution',
      title: `${String(awaiting)} payment${awaiting === 1 ? ' is' : 's are'} submitted with no transfer reference`,
      body: 'The institution has returned neither a reference nor a decision, so these are pending and not sent. There is no SLA in this system, so how long is too long is a judgement rather than a threshold.',
      ref: 'state = submitted, transfer_ref absent',
    })
  }

  blocks.push({
    t: 'table', title: 'Cash-outs', src: 'ledger', facets: false,
    cols: CASH_OUT_COLS, rows, footer,
  })

  // The queue depths, over the whole table — and ABSENT is not six zeroes.
  // A screen that drew zeroes would report an empty queue on the strength of a
  // question nobody asked.
  const counted = readBool(o, 'by_state_counted')
  const byState = asArray(o['by_state'])
  if (counted === true && byState !== null) {
    const depths: string[] = []
    for (const entry of byState) {
      const s = asRecord(entry)
      if (s === null) continue
      const name = readText(s, 'state')
      const count = readInt(s, 'count')
      if (name === null) continue
      depths.push(`${name} ${count.kind === 'exact' ? String(count.value) : '—'}`)
    }
    if (depths.length > 0) {
      blocks.push({
        t: 'note', tone: 'n', kicker: 'Queue depth',
        title: 'Over the whole table, not this page',
        body: depths.join(' · '),
        ref: 'by_state · every state including the ones at zero',
      })
    }
  } else if (counted === false) {
    blocks.push({
      t: 'note', tone: 'n', kicker: 'Queue depth',
      title: 'The depths were not counted for this read',
      body: 'The endpoint is saying nothing about how many payments sit in each state. That is not a queue of zero — it is a question nobody asked.',
      ref: 'by_state_counted = false',
    })
  }

  // Why there is no SCA column, in the backend's own words.
  const sca = disclosure(o, 'sca_note', 'SCA', 'The SCA lifecycle is unrecorded, not unread', 'n')
  if (sca !== null && rows.length > 0) blocks.push(sca)

  const rTransaction = disclosure(
    o, 'r_transaction_note', 'Returns', 'There is no R-transaction reason here', 'n',
  )
  if (rTransaction !== null && rows.length > 0) blocks.push(rTransaction)

  const missing = [...CASH_OUT_MISSING, ...summarise(gaps)]
  if (counted !== true) missing.push('the per-state queue depths — this read did not count them')
  if (unreadable > 0) missing.push(`${String(unreadable)} payment(s) this console could not read`)

  return {
    blocks,
    provenance: {
      kind: 'partial', source: CASH_OUTS_SOURCE, authority: CASH_OUT_AUTHORITY,
      readAt: receipt(at), missing,
    },
    title: 'Cash-outs',
    subject: `cash_outs · ${String(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}`,
    rowKeys,
  }
}

export const loadCashOuts: ScreenLoader = async (api) =>
  composeCashOuts(
    await attempt(() => api.get<unknown>(CASH_OUTS_PATH), 'The cash-out queue'),
    new Date(),
  )

// ---------------------------------------------------------------------------
// One payment — the row opener
// ---------------------------------------------------------------------------

export function composeCashOut(read: Attempt, id: string, at: Date): ScreenData {
  if (!read.ok) {
    return unread(
      'Cash-out', 'This payment could not be read',
      'Nothing about it is known — including whether it exists.',
      read, CASH_OUTS_SOURCE,
    )
  }

  const o = asRecord(read.payload)
  const payment = o === null ? null : asRecord(o['cash_out'])
  if (o === null || payment === null) {
    return unread(
      'Cash-out', 'This payment could not be read',
      'The payment returned a shape this console does not know.',
      malformed('The payment returned a shape this console does not know.'), CASH_OUTS_SOURCE,
    )
  }

  const gaps: string[] = []
  const amount = moneyCell(payment, 'amount')
  if (!moneyReadable(payment, 'amount')) gaps.push('an amount')
  const amountText = Array.isArray(amount) && typeof amount[0] === 'number' && typeof amount[1] === 'string'
    ? `${String(amount[0])} minor · ${amount[1]}`
    : typeof amount === 'string' ? amount : '—'

  const state = readText(payment, 'state')
  const transfer = readText(payment, 'transfer_ref')
  const decision = readText(payment, 'decision_ref')
  const decline = readText(payment, 'decline_reason')
  const submitted = readInstant(payment, 'submitted_at')
  const settled = readInstant(payment, 'settled_at')
  const challenge = readText(payment, 'challenge_ref')
  const scaCompleted = readInstant(payment, 'sca_completed_at')

  const items: KvItem[] = [
    ['Payment', readInt(payment, 'id').kind === 'exact' ? id : '— no id', '', 'n', '', 'm'],
    ['Customer', readInt(payment, 'customer_id').kind === 'exact'
      ? String((readInt(payment, 'customer_id') as { value: number }).value) : '—', '', 'n', '', 'm'],
    ['Amount', amountText, '', 'n', '', 'b'],
    ['State', state === 'abandoned' ? 'abandoned · our 48h sweep ran' : (state ?? '— unstated'), '', state === null ? 'a' : 'n', '', ''],
    ['Beneficiary', readText(payment, 'creditor_name') ?? '— not stated', '', 'n', '', ''],
    ['Idempotency key', readText(payment, 'idempotency_key') ?? '— none', '', 'n',
      'the key to quote when asking the institution what happened to this payment', 'm'],
    [
      'Provider transfer',
      transfer ?? '— the institution has returned none',
      '', transfer === null ? 'a' : 'n',
      // Law 2: a pending instruction with no provider reference renders as
      // pending, never as done.
      transfer === null ? 'without one this payment is pending, never sent' : '',
      'm',
    ],
    ['decision_ref', decision ?? '— none quoted', '', decision === null ? 'a' : 'n',
      'the reference to quote when a customer asks why', 'm'],
    ['Submitted', submitted.kind === 'at' ? submitted.label : 'not submitted', '', 'n', '', 'm'],
    ['Settled', settled.kind === 'at' ? settled.label : 'not settled', '', 'n', '', 'm'],
    // Rendered as the two raw facts they are, with no verdict drawn from them.
    // A completion stamp records when WE OBSERVED THE INSTITUTION REPORT one —
    // never when the customer touched their device — and a null is an absence
    // of observation, not a failure.
    ['Strong authentication', challenge === null
      ? '— no challenge reference recorded'
      : scaCompleted.kind === 'at'
        ? `challenge ${challenge} · we observed a completion reported ${scaCompleted.label}`
        : `challenge ${challenge} · no completion observed`,
      '', 'n',
      'there is no SCA state in this system: this is a reference and a stamp, and neither is a pass or a failure',
      ''],
  ]
  if (decline !== null) {
    items.push(['Decline reason', decline, '', 'a',
      'the institution’s own words, verbatim — never a code and never reworded', ''])
  }

  const blocks: Block[] = [{ t: 'kv', title: 'The payment', src: 'ledger', items }]

  const sca = disclosure(o, 'sca_note', 'SCA', 'The SCA lifecycle is unrecorded, not unread', 'n')
  if (sca !== null) blocks.push(sca)

  const control = disclosure(o, 'control_note', 'Controls', 'Nothing here moves this payment', 'n')
  if (control !== null) blocks.push(control)

  return {
    blocks,
    provenance: {
      kind: 'partial', source: CASH_OUTS_SOURCE, authority: CASH_OUT_AUTHORITY,
      readAt: receipt(at),
      missing: [...CASH_OUT_MISSING, ...summarise(gaps)],
    },
    title: `Cash-out ${id}`,
    subject: 'cash_outs · 1 row',
  }
}

/** One payment, opened from a queue row. */
export async function readCashOut(api: ApiClient, id: string): Promise<ScreenData> {
  return composeCashOut(
    await attempt(() => api.get<unknown>(cashOutPath(id)), 'The payment'),
    id,
    new Date(),
  )
}
